import { memo, useCallback, useEffect, useRef } from 'react';

import { cn } from '@air/utils-tailwind';

import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';

type SmartSearchPromotionDropdownProps = {
  onLearnMoreClick: () => void;
};

const SMART_SEARCH_EXAMPLE_SEARCH_PHRASES = [
  'videos of dogs running',
  'cats chasing laser pointers',
  'happy faces wearing colorful hats',
  'children playing in the park',
  'sports action shots',
  'beautiful dance performances',
];

export const SmartSearchPromotionDropdown = memo(({ onLearnMoreClick }: SmartSearchPromotionDropdownProps) => {
  const { ui2026 } = useTemporaryFeatureFlags();
  const containerRef = useRef<HTMLDivElement>(null);
  const pillContainerRef = useRef<HTMLDivElement>(null);
  const parentContainerRef = useRef<HTMLDivElement>(null);

  const updatePillContainerDimensions = useCallback(() => {
    if (ui2026 || !pillContainerRef.current || !containerRef.current || !parentContainerRef.current) {
      return;
    }

    const pillContainerBounds = pillContainerRef.current.getBoundingClientRect();

    if (pillContainerBounds.width > 0) {
      pillContainerRef.current.style.setProperty(
        '--pill-left-offset',
        `calc(50% - ${pillContainerBounds.width / 2}px)`,
      );

      if (pillContainerBounds.height > 0) {
        const computedStyle = getComputedStyle(parentContainerRef.current);
        const paddingTop = parseFloat(computedStyle.paddingTop);
        const paddingBottom = parseFloat(computedStyle.paddingBottom);
        const totalPadding = paddingTop + paddingBottom;

        const totalHeight = Math.max(totalPadding, pillContainerBounds.height + totalPadding);
        parentContainerRef.current.style.setProperty('--parent-container-height', `${totalHeight}px`);
      }
    }
  }, [ui2026]);

  useEffect(() => {
    if (ui2026 || !containerRef.current) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        updatePillContainerDimensions();
      });
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [ui2026, updatePillContainerDimensions]);

  const examplePills = SMART_SEARCH_EXAMPLE_SEARCH_PHRASES.map((pill) => (
    <div className="relative flex items-center justify-center rounded-[100px] bg-gray-1/40 px-4 py-2" key={pill}>
      <div className="pointer-events-none absolute inset-0 rounded-[100px] border border-gray-1 [mask-composite:intersect] [mask-image:radial-gradient(circle_at_100%_0%,transparent_7.5%,white_15%),radial-gradient(circle_at_0%_100%,transparent_7.5%,white_15%)]"></div>
      <div className="justify-center font-semibold text-14 text-purple-10 leading-tight">&ldquo;{pill}&rdquo;</div>
    </div>
  ));

  return (
    <div
      data-theme="light"
      className={cn(
        'relative grow overflow-y-auto',
        ui2026 ? 'min-h-0 w-full' : 'size-full rounded-8 border border-gray-5 bg-gray-1',
      )}
      data-testid="SMART_SEARCH_PROMOTION_DROPDOWN"
      ref={containerRef}
    >
      <div
        className={cn(
          'inline-flex w-full flex-col items-start justify-start [background:linear-gradient(180deg,#E4DEFC_50%,#FFF_85%,#FFF_100%)]',
          ui2026 ? 'overflow-visible' : 'overflow-hidden rounded-8 outline outline-1 outline-gray-5',
        )}
      >
        {ui2026 ? (
          <div className="flex w-full justify-center px-4 pt-3 pb-1">
            <div className="inline-flex max-w-[600px] flex-wrap place-content-center items-center gap-2">
              {examplePills}
            </div>
          </div>
        ) : (
          <div
            className="pointer-events-none relative self-stretch rounded-[7px] border border-gray-5 [height:var(--parent-container-height,auto)]"
            ref={parentContainerRef}
          >
            <div
              className="absolute -top-[16px] inline-flex w-[600px] flex-wrap place-content-center items-center gap-2 [left:var(--pill-left-offset,50%)]"
              ref={pillContainerRef}
            >
              {examplePills}
            </div>
          </div>
        )}
        <div
          className={cn(
            'flex flex-col items-center justify-center gap-2 self-stretch px-4 pb-4',
            ui2026 ? 'overflow-visible pt-1' : 'overflow-hidden pt-2',
          )}
        >
          <div className="flex w-[340px] flex-col gap-2">
            <div className="flex flex-col items-center justify-center gap-1 self-stretch">
              <div className="inline-flex items-center justify-center self-stretch">
                <div className="justify-center">
                  <span className="inline font-control-upright font-normal text-[19px] text-gray-12 leading-[20px] tracking-tight">
                    Search how you <span className="font-control tracking-[1px]">think.</span>
                  </span>
                </div>
              </div>
              <div className="justify-center self-stretch text-center font-normal text-14 text-gray-12 leading-[20px] tracking-tight">
                Smart search expands your search using AI to find exactly what you&rsquo;re looking for&mdash;like
                magic.
              </div>
              <div className="flex flex-col items-start justify-start rounded">
                <button
                  className="inline-flex items-center justify-center gap-1 rounded bg-gray-1 px-2 py-[3px] text-center font-semibold text-12 text-gray-12 leading-[18px] tracking-tight outline outline-1 outline-gray-5 outline-offset-[-1px] hover:bg-gray-3"
                  onClick={onLearnMoreClick}
                  type="button"
                >
                  Learn more
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

SmartSearchPromotionDropdown.displayName = 'SmartSearchPromotionDropdown';
